import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { ICellRendererParams } from 'ag-grid-community';

import DeviceStatusIcon from '@/components/DeviceStatusIcon';

import useStyles from '../DevicesTable.styles';
import type { AdminTreeDataRow } from '../utils/adminTreeData.utils';

/**
 * Cell renderer for device name column in admin devices view
 *
 * Displays device status icon with tooltip and device name.
 * Shows display name in brackets if present, otherwise shows hostname.
 * Skips rendering for group rows.
 *
 * @param props - AG Grid cell renderer params
 * @returns JSX element with device icon and name
 */
const AdminDeviceNameCellRenderer = (
  props: ICellRendererParams<AdminTreeDataRow>
) => {
  const { classes } = useStyles();
  const { data } = props;

  if (!data || data.isGroupRow) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Tooltip
        arrow
        placement="right"
        title={data.status_label || ''}
        classes={{
          arrow: classes.routeButtonTooltipArrow,
          tooltip: classes.routeButtonTooltip
        }}
      >
        <Box
          sx={{ display: 'inline-flex', alignItems: 'center' }}
          className={classes.deviceStatusIconStyle}
        >
          <DeviceStatusIcon
            deviceType={data.device_type?.type || ''}
            status={data.status || ''}
          />
        </Box>
      </Tooltip>

      <Box className={classes.icoLabelStyle}>
        {data.display_name && data.display_name.length > 0 ? (
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
            {data.host_name}
            <Typography
              style={{ alignContent: 'center' }}
              color="text.secondary"
            >
              [{data.display_name}]
            </Typography>
          </Stack>
        ) : (
          data.host_name
        )}
      </Box>
    </Stack>
  );
};

export default AdminDeviceNameCellRenderer;
